"use client";

import { useEffect, useState } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion } from "motion/react";
import { ChevronRight, Calendar, User, Book } from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { FadeInSection } from "@/components/common/FadeInSection";
import { ICMS } from "@/slices/homeSlice";
import { IBlog } from "@/slices/blogSlice";
import { useAppSelector } from "@/store/hooks";
import {
  calculateReadingTime,
  cleanHeaderTags,
  createLookupObject,
} from "@/_helper/helper";
import { Banner } from "../common/Banner";

export default function BlogListClient({
  BlogList,
  CMSList,
  initialBanner,
}: {
  BlogList: IBlog[];
  CMSList: ICMS[];
  initialBanner?: any;
}) {
  // const [selectedCategory, setSelectedCategory] = useState("All");
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    initialBanner ||
    BannerList?.find((banner: any) => banner?.BannerType === "blog") ||
    {};
  useEffect(() => {
    if (CMSList && CMSList.length > 0) {
      setLookUpCMSData(createLookupObject(CMSList, "Name"));
    }
  }, [CMSList]);

  const filteredBlogs =
    // selectedCategory === "All"
    //   ?
    BlogList;
  // : BlogList.filter((blog) => blog.BlogCategoryName === selectedCategory);

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Hero Section */}
      {/* <section className="pt-40 pb-16 bg-gradient-to-br from-[#2a2520] via-[#3a3530] to-[#2a2520] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
              }}
            >
              Our{" "}
              <span style={{ fontWeight: 600, fontStyle: "italic" }}>Blog</span>
            </h1>
            <p
              className="text-xl text-white/90 max-w-3xl mx-auto"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Travel Insights, Luxury Experiences & Destination Guides
            </p>
          </motion.div>
        </div>
      </section> */}
      <Banner
        title={banner?.BannerTitle || "Our Blog"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        // icon={<Book className="w-16 h-16 text-[#d4af37]" />}
      />

      {/* Categories */}
      {/* <section className="py-8 bg-white shadow-sm sticky top-0 sm:top-20 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <motion.button
                key={category}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 transition-all ${
                  selectedCategory === category
                    ? "bg-[#5a6f3e] text-white shadow-lg"
                    : "bg-[#f5f1ec] text-[#2a2520] hover:bg-[#5a6f3e]/10"
                }`}
                style={{
                  fontFamily: "Jost, sans-serif",
                  fontWeight: selectedCategory === category ? 600 : 400,
                }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section> */}

      {/* Blog Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog, index) => (
              <FadeInSection key={blog.ID} delay={index * 0.1}>
                <Link
                  href={`/blogs/${blog.BlogURL}`}
                  aria-label={`Read more about ${blog.Title}`}
                >
                  <motion.article
                    whileHover={{ y: -8 }}
                    className="bg-white shadow-xl overflow-hidden group h-full flex flex-col"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <Image
                        src={blog.Image}
                        alt={blog.Title}
                        fill
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      {/* <div className="absolute top-4 left-4">
                        <span
                          className="bg-[#d4af37] text-[#2a2520] px-3 py-1 text-xs font-semibold"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          {blog.category}
                        </span>
                      </div> */}
                    </div>

                    <div className="p-6 flex-1 flex flex-col">
                      <div
                        className="flex items-center gap-4 text-xs text-[#2a2520]/60 mb-3"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {blog.BlogDate}
                        </div>
                        {/* <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {blog.author}
                        </div> */}
                      </div>

                      <h2
                        className="text-xl md:text-2xl text-[#2a2520] mb-3 group-hover:text-[#5a6f3e] transition-colors leading-tight"
                        style={{
                          fontFamily: "Cormorant Garamond, serif",
                          fontWeight: 600,
                        }}
                      >
                        {blog.Title}
                      </h2>

                      <p
                        className="text-[#2a2520]/70 mb-4 flex-1 line-clamp-3"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                        dangerouslySetInnerHTML={{
                          __html: cleanHeaderTags(blog.ShortDescription || ""),
                        }}
                      ></p>

                      <div className="flex items-center justify-between pt-4 border-t border-[#5a6f3e]/10">
                        <span
                          className="text-xs text-[#2a2520]/60"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          {calculateReadingTime(blog.Description || "")}
                        </span>
                        <div
                          className="flex items-center gap-2 text-[#5a6f3e] font-semibold"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Read More
                          <span className="sr-only"> about {blog.Title}</span>
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </motion.article>
                </Link>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {lookUpCMSData["Blogs - Section 1 : CTA"] && (
        <section className="py-20 bg-gradient-to-br from-[#5a6f3e] to-[#4a5f2e] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData["Blogs - Section 1 : CTA"].Template || "",
                  ),
                }}
              ></div>
              {/* <h2
              className="text-4xl md:text-5xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              Plan Your Stay at Sarasiruham Resort Today
            </h2>
            <p
              className="text-xl text-white/90 mb-10"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Experience luxury, comfort, and authentic Rajasthani hospitality
            </p> */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Link
                    href="/rooms"
                    className="bg-[#d4af37] text-[#2a2520] px-10 py-4 shadow-xl hover:bg-[#c9a961] transition-colors text-lg inline-block h-full"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                  >
                    Explore Rooms
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Link
                    href="/contact"
                    className="border-2 border-white text-white px-10 py-4 hover:bg-white hover:text-[#5a6f3e] transition-all text-lg inline-block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                  >
                    Contact Us
                  </Link>
                </motion.div>
              </div>
            </FadeInSection>
          </div>
        </section>
      )}
    </div>
  );
}
